import React, { useEffect, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import NotificationController from '../Controllers/NotificationController';
import apiClient from '../Utils/apiClient';
import { toastSuccess, toastError } from '../Utils/toast';

const PAGE_SIZE = 20;

const SEVERITY_ICON = {
  info: 'bi-info-circle',
  warning: 'bi-exclamation-triangle',
  critical: 'bi-exclamation-octagon',
};

const EMPTY_FORM = {
  id: null,
  title: '',
  message: '',
  severity: 'info',
  companyIds: [],
  scheduledAt: '',
};

export default function Notifications() {
  const { auth } = useAuth();
  const isManager = ['Admin', 'SuperAdmin'].includes(auth?.role);

  const [result, setResult] = useState({ data: [], total: 0 });
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [companies, setCompanies] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (unreadOnly) params.unread = 'true';
      const data = isManager
        ? await NotificationController.manageList(params)
        : await NotificationController.list(params);
      setResult(data);
    } catch (err) {
      setError('Failed to load notifications. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly, isManager]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isManager) return;
    apiClient.get('companies')
      .then((res) => setCompanies(res.data?.data || res.data || []))
      .catch((err) => console.error('Failed to load companies', err));
  }, [isManager]);

  const handleMarkRead = async (id) => {
    try {
      await NotificationController.markRead(id);
      setResult((prev) => ({
        ...prev,
        data: prev.data.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
      }));
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await NotificationController.markAllRead();
      toastSuccess('All notifications marked as read');
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all notifications read', err);
      toastError('Failed to mark notifications as read');
    }
  };

  const openCreateForm = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (item) => {
    setForm({
      id: item.id,
      title: item.title,
      message: item.message || '',
      severity: item.severity,
      companyIds: item.company_id ? [item.company_id] : [],
      scheduledAt: item.scheduled_at ? item.scheduled_at.slice(0, 16) : '',
    });
    setFormOpen(true);
  };

  const toggleCompany = (id) => {
    setForm((prev) => ({
      ...prev,
      companyIds: prev.companyIds.includes(id)
        ? prev.companyIds.filter((c) => c !== id)
        : [...prev.companyIds, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toastError('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await NotificationController.update(form.id, {
          title: form.title,
          message: form.message,
          severity: form.severity,
          scheduled_at: form.scheduledAt || null,
        });
        toastSuccess('Notification updated');
      } else {
        await NotificationController.create({
          company_ids: form.companyIds,
          title: form.title,
          message: form.message,
          severity: form.severity,
          scheduled_at: form.scheduledAt || null,
        });
        toastSuccess(
          form.companyIds.length
            ? `Notification sent to ${form.companyIds.length} compan${form.companyIds.length > 1 ? 'ies' : 'y'}`
            : 'Platform-wide notification created'
        );
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      fetchNotifications();
    } catch (err) {
      console.error('Failed to save notification', err);
      toastError(err.response?.data?.message || 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this notification?',
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await NotificationController.remove(id);
      toastSuccess('Notification deleted');
      fetchNotifications();
    } catch (err) {
      console.error('Failed to delete notification', err);
      toastError(err.response?.data?.message || 'Failed to delete notification');
    }
  };

  const totalPages = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE));
  const isScheduledFuture = (item) => item.scheduled_at && new Date(item.scheduled_at) > new Date();

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">Notifications</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              {result.total} total
            </p>
          </div>

          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <label className="d-flex align-items-center" style={{ gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => {
                  setPage(1);
                  setUnreadOnly(e.target.checked);
                }}
              />
              Unread only
            </label>
            <button className="ivs-btn-primary" type="button" onClick={handleMarkAllRead}>
              Mark all as read
            </button>
            {isManager && (
              <button className="ivs-btn-primary" type="button" onClick={openCreateForm}>
                <i className="bi bi-plus-lg mr-1"></i> New Notification
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {isManager && formOpen && (
          <form className="ivs-search-panel" onSubmit={handleSubmit}>
            <h3 className="ivs-section-title" style={{ marginBottom: 16 }}>
              {form.id ? 'Edit Notification' : 'New Notification'}
            </h3>

            <div className="ivs-search-row" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 16 }}>
              <div>
                <label className="ivs-field-label">Title</label>
                <input
                  className="ivs-field"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Scheduled maintenance tonight"
                  required
                />
              </div>
              <div>
                <label className="ivs-field-label">Severity</label>
                <select
                  className="ivs-field"
                  value={form.severity}
                  onChange={(e) => setForm((p) => ({ ...p, severity: e.target.value }))}
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="ivs-field-label">Message</label>
              <textarea
                className="ivs-field"
                rows={3}
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                placeholder="Optional details"
              />
            </div>

            <div className="ivs-search-row" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 16 }}>
              <div>
                <label className="ivs-field-label">
                  Companies {form.id && <span className="text-muted">(fixed after creation)</span>}
                </label>
                {form.id ? (
                  <div className="ivs-field" style={{ background: 'var(--ivs-bg)' }}>
                    {form.companyIds.length
                      ? companies.find((c) => c.id === form.companyIds[0])?.name_firma || `Company #${form.companyIds[0]}`
                      : 'Platform-wide'}
                  </div>
                ) : (
                  <div
                    className="ivs-field"
                    style={{ height: 'auto', maxHeight: 140, overflowY: 'auto', padding: '8px 14px' }}
                  >
                    {companies.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>Loading companies…</div>}
                    {companies.map((c) => (
                      <label key={c.id} className="d-flex align-items-center" style={{ gap: 8, padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={form.companyIds.includes(c.id)}
                          onChange={() => toggleCompany(c.id)}
                        />
                        {c.name_firma} ({c.short_name})
                      </label>
                    ))}
                    {companies.length > 0 && (
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                        None selected = platform-wide (visible to every company)
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="ivs-field-label">Schedule for later (optional)</label>
                <input
                  type="datetime-local"
                  className="ivs-field"
                  value={form.scheduledAt}
                  onChange={(e) => setForm((p) => ({ ...p, scheduledAt: e.target.value }))}
                />
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Leave blank to show immediately
                </div>
              </div>
            </div>

            <div className="d-flex" style={{ gap: 10 }}>
              <button className="ivs-btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create Notification'}
              </button>
              <button
                type="button"
                className="ivs-pagination__btn"
                onClick={() => { setFormOpen(false); setForm(EMPTY_FORM); }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="ivs-panel" style={{ padding: 0 }}>
          {loading ? (
            <div className="ivs-empty-state">Loading…</div>
          ) : result.data.length === 0 ? (
            <div className="ivs-empty-state">No notifications found</div>
          ) : (
            result.data.map((item) => (
              <div key={item.id} className="ivs-step" style={{ padding: '16px 20px', background: !item.read_at ? 'var(--ivs-primary-light)' : 'transparent' }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <i className={`bi ${SEVERITY_ICON[item.severity] || 'bi-info-circle'}`} style={{ fontSize: 18, marginTop: 2, color: item.severity === 'critical' ? 'var(--ivs-danger)' : item.severity === 'warning' ? 'var(--ivs-warning)' : 'var(--ivs-text-muted)' }}></i>
                  <div>
                    <div className="ivs-step__label">
                      {item.title}
                      {isScheduledFuture(item) && (
                        <span className="ivs-notif__badge" style={{ marginLeft: 8, background: 'var(--ivs-warning)' }}>
                          Scheduled for {new Date(item.scheduled_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {item.message && <div className="ivs-step__meta">{item.message}</div>}
                    <div className="ivs-step__meta">
                      {item.company_name || (item.company_id ? `Company #${item.company_id}` : 'Platform-wide')} · {new Date(item.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                  {!item.read_at && (
                    <button className="ivs-icon-btn" type="button" onClick={() => handleMarkRead(item.id)} aria-label="Mark as read">
                      <i className="bi bi-check-lg"></i>
                    </button>
                  )}
                  {isManager && (
                    <>
                      <button className="ivs-icon-btn" type="button" onClick={() => openEditForm(item)} aria-label="Edit">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button className="ivs-icon-btn" type="button" onClick={() => handleDelete(item.id)} aria-label="Delete">
                        <i className="bi bi-trash"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}

          {result.total > 0 && (
            <div className="ivs-pagination" style={{ padding: '16px 20px' }}>
              <button
                className="ivs-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="ivs-pagination__label">
                Page {page} of {totalPages}
              </span>
              <button
                className="ivs-pagination__btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
